import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";

// GraphQL query
const APP_ESSENTIALS_GET_QUERY = gql`
  query AppEssentialsGet {
    appEssentialsGet {
      code
      data {
        lang
        carBodyTypes {
          id
          name
        }
        carEngineTypes {
          id
          code
          name
        }
        carTransmissionTypes {
          id
          code
          name
        }
        vehicleMakes {
          id
          makeName
        }
        expenseCategories {
          id
          code
          name
          kinds {
            id
            code
            canSchedule
            isItMaintenance
            name
          }
        }
        revenueCategories {
          id
          code
          name
          kinds {
            id
            code
            name
          }
        }
        gloveboxDocTypes {
          id
          code
          category
          orderNo
          hasDocumentNumber
          hasIssueDate
          hasEffectiveDate
          hasExpiration
          hasIssuingAuthority
          hasCost
          hasCoverageAmount
          hasInspectionDate
          documentNumberLabelKey
          name
          description
          documentNumberLabel
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface CarBodyType {
  id?: number | undefined;
  name?: string | undefined;
}

export interface CarEngineType {
  id?: number | undefined;
  code?: string | undefined;
  name?: string | undefined;
}

export interface CarTransmissionType {
  id?: number | undefined;
  code?: string | undefined;
  name?: string | undefined;
}

export interface VehicleMake {
  id?: number | undefined;
  makeName?: string | undefined;
}

export interface ExpenseKind {
  id?: number | undefined;
  code?: string | undefined;
  canSchedule?: boolean | undefined;
  isItMaintenance?: boolean | undefined;
  name?: string | undefined;
}

export interface ExpenseCategory {
  id?: number | undefined;
  code?: string | undefined;
  name?: string | undefined;
  kinds?: ExpenseKind[] | undefined;
}

export interface RevenueKind {
  id?: number | undefined;
  code?: string | undefined;
  name?: string | undefined;
}

export interface RevenueCategory {
  id?: number | undefined;
  code?: string | undefined;
  name?: string | undefined;
  kinds?: RevenueKind[] | undefined;
}

export type GloveboxDocCategory = 'vehicle' | 'driver' | 'equipment' | 'other';

export interface GloveboxDocType {
  id?: number | undefined;
  code?: string | undefined;
  category?: GloveboxDocCategory | undefined;
  orderNo?: number | undefined;

  // Field visibility flags
  hasDocumentNumber?: boolean | undefined;
  hasIssueDate?: boolean | undefined;
  hasEffectiveDate?: boolean | undefined;
  hasExpiration?: boolean | undefined;
  hasIssuingAuthority?: boolean | undefined;
  hasCost?: boolean | undefined;
  hasCoverageAmount?: boolean | undefined;
  hasInspectionDate?: boolean | undefined;
  hasPhone?: boolean | undefined;
  hasWebsite?: boolean | undefined;

  // Label customization
  documentNumberLabelKey?: string | undefined;

  // Localized fields
  name?: string | undefined;
  description?: string | undefined;
  documentNumberLabel?: string | undefined;
}

export interface AppEssentials {
  lang?: string | undefined;
  carBodyTypes?: CarBodyType[] | undefined;
  carEngineTypes?: CarEngineType[] | undefined;
  carTransmissionTypes?: CarTransmissionType[] | undefined;
  vehicleMakes?: VehicleMake[] | undefined;
  expenseCategories?: ExpenseCategory[] | undefined;
  revenueCategories?: RevenueCategory[] | undefined;
  gloveboxDocTypes?: GloveboxDocType[] | undefined;
}

/**
 * AppEssentials service that fetches all essential lookup dictionaries in a single call.
 * This includes car body types, engine types, transmission types, vehicle makes,
 * expense categories, revenue categories, and glovebox document types.
 */
export class AppEssentialsService {
  /**
   * Get all essential lookup data for the application
   */
  static async get(): Promise<OpResult> {
    const result = await ApiService.executeQuery(APP_ESSENTIALS_GET_QUERY);
    return result;
  }
}